import { SECTIONS } from '@/types/sections'
import { useApi } from '@/composables/api.ts'
import type { Sections } from '@/types/sections'
import type {
  NodeContent,
  NodeContentParams,
  NodeInfo,
} from '@deepcloud/api-deep-box'
import type { RouteLocationNamedRaw } from 'vue-router'

export const useDeepBoxPreviewStore = defineStore('deepBoxPreview', () => {
  const { deepBoxAPI } = useApi()

  const parentNodeChildren = ref<NodeContent | undefined>(undefined)
  const fetchParentNodeChildrenPending = ref(false)
  const backRoute = ref<RouteLocationNamedRaw>()
  const currentNodeData = ref<NodeInfo>()

  async function fetchParentNodeChildren({
    section,
    typeId,
    boxId,
    nodeId,
    params = {
      limit: 50,
    },
  }: {
    section: Sections
    typeId: string
    boxId: string
    nodeId: string
    params?: NodeContentParams
  }) {
    fetchParentNodeChildrenPending.value = true
    let res
    try {
      switch (section) {
        case SECTIONS.QUEUE:
          res = await deepBoxAPI.value.deepBoxesBoxesQueue.get(
            typeId,
            boxId,
            params,
          )
          break
        case SECTIONS.FILES:
          res = await deepBoxAPI.value.deepBoxesBoxesFiles.get(
            typeId,
            boxId,
            params,
          )
          break
        case SECTIONS.FILE_NODES:
          res = await deepBoxAPI.value.deepBoxesBoxesFiles.getById(
            typeId,
            boxId,
            nodeId,
            params,
          )
          break
        case SECTIONS.TRASH:
          res = await deepBoxAPI.value.deepBoxesBoxesTrash.get(
            typeId,
            boxId,
            params,
          )
          break
        case SECTIONS.TRASH_NODES:
          res = await deepBoxAPI.value.deepBoxesBoxesTrash.getById(
            typeId,
            boxId,
            nodeId,
            params,
          )
          break

        // no default
      }

      if (res) {
        if (params.offset && params.offset > 0) {
          // append to existing nodes
          parentNodeChildren.value = {
            ...res.data,
            nodes: [
              ...(parentNodeChildren.value?.nodes ?? []),
              ...(res.data.nodes ?? []),
            ],
          }
        } else {
          // initial load, replace
          parentNodeChildren.value = { ...res.data }
        }
      }

      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    } finally {
      fetchParentNodeChildrenPending.value = false
    }
  }

  function setBackRoute(route: RouteLocationNamedRaw | undefined) {
    backRoute.value = route
  }

  function setCurrentNodeData(value: NodeInfo) {
    currentNodeData.value = value
  }

  return {
    // state
    fetchParentNodeChildrenPending,
    parentNodeChildren,
    backRoute,
    currentNodeData,
    // actions
    fetchParentNodeChildren,
    setBackRoute,
    setCurrentNodeData,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useDeepBoxPreviewStore, import.meta.hot),
  )
}
